/**
 * PremiumService — Système d'abonnements à 4 niveaux (tiers)
 *
 *  ┌─────────┬──────────────┬──────────┬───────┬──────┬────────┬──────────┐
 *  │ Tier    │ Prix /mois   │ Dressing │ IA/j  │ Looks│ Voyage │ Cashback │
 *  ├─────────┼──────────────┼──────────┼───────┼──────┼────────┼──────────┤
 *  │ free    │   0,00 €     │    10    │   3   │  3   │   ❌   │   5%     │
 *  │ basic   │   4,99 €     │    30    │  10   │ 10   │  ✅ 3   │  10%     │
 *  │ premium │   8,99 €     │   100    │  30   │  ∞   │   ∞    │  15%     │
 *  │ gold    │  14,99 €     │    ∞     │   ∞   │  ∞   │   ∞    │  25%     │
 *  └─────────┴──────────────┴──────────┴───────┴──────┴────────┴──────────┘
 *
 *  Paiement annuel : ~17% de réduction, prélevé en 1× par an
 *    basic_yearly   :  4,16 €/mois  =  49,90 €/an  (-17%)
 *    premium_yearly :  7,49 €/mois  =  89,90 €/an  (-17%)
 *    gold_yearly    : 12,49 €/mois  = 149,90 €/an  (-17%)
 */
import AsyncStorage from '@react-native-async-storage/async-storage';

// ═══ Définition des tiers ═══════════════════════════════════════
// Barème optimisé marketing : Free frustrant + Basic accessible (<5€) +
// Premium "le plus choisi" comme plan d'ancrage + Gold premium VIP.
export const TIERS = {
  free: {
    id: 'free',
    label: 'Gratuit',
    priceMonthly: 0,
    priceYearly:  0,
    wardrobe_items: 20,
    ai_requests:    5,
    looks_per_day:  5,
    trips_access:   false,
    trips_count:    0,
    tryons_per_month: 0,
    color:          '#999',
    cashback: { redistributionRate: 0.10, displayMaxEurPer100: 1.20 },
  },
  basic: {
    id: 'basic',
    label: 'Basic',
    priceMonthly: 5.99,
    priceYearly:  59.90,
    priceYearlyMonth: 4.99,
    wardrobe_items: 50,
    ai_requests:    20,
    looks_per_day:  15,
    trips_access:   true,
    trips_count:    3,
    tryons_per_month: 5,
    color:          '#6C9AC4',
    cashback: { redistributionRate: 0.15, displayMaxEurPer100: 1.80 },
  },
  premium: {
    id: 'premium',
    label: 'Premium',
    priceMonthly: 9.99,
    priceYearly:  99.90,
    priceYearlyMonth: 8.32,
    wardrobe_items: 150,   // v5 : anti-cannibalisation Gold
    ai_requests:    50,
    looks_per_day:  Infinity,
    trips_access:   true,
    trips_count:    Infinity,
    tryons_per_month: 15,
    color:          '#B8860B',
    popular:        true,
    cashback: { redistributionRate: 0.25, displayMaxEurPer100: 3.00 },
  },
  gold: {
    id: 'gold',
    label: 'Gold',
    priceMonthly: 16.99,
    priceYearly:  169.90,
    priceYearlyMonth: 14.16,
    wardrobe_items: 500,
    ai_requests:    100,
    looks_per_day:  Infinity,
    trips_access:   true,
    trips_count:    Infinity,
    tryons_per_month: 40,
    color:          '#D4AF37',
    cashback: { redistributionRate: 0.40, displayMaxEurPer100: 4.80 },
  },
};

// Cashback par tier : nouveau barème v1.12.313 (non affiché en UI)
// Source de vérité pour CashbackConfig.js
const CASHBACK_NEW = {
  free:    0.10,
  basic:   0.15,
  premium: 0.25,
  gold:    0.40,
};

const PAID_TIERS = ['basic', 'premium', 'gold'];

// ═══ Compat : anciennes variables utilisées par le reste du code ═══
export const FREE_LIMITS = {
  wardrobe_items: TIERS.free.wardrobe_items,
  ai_requests:    TIERS.free.ai_requests,
  looks_per_day:  TIERS.free.looks_per_day,
  trips:          TIERS.free.trips_count,
  tryons:         TIERS.free.tryons_per_month, // v1.10.0
};

// ═══ Plans proposés dans l'UI ═══
// ═══ FIX 2026-04 : prix alignés sur TIERS (source de vérité) ═══
// Avant : PREMIUM_PLANS (5,99 / 7,99 / 9,99) ne matchaient pas TIERS
//   (4,99 / 8,99 / 14,99) → confusion entre l'écran d'achat et la logique
//   métier. Maintenant, on dérive directement depuis TIERS pour rester
//   cohérent : si on change un prix dans TIERS, l'UI suit automatiquement.
export const PREMIUM_PLANS = [
  { id: 'basic',   tier: 'basic',   label: 'Basic',   price: TIERS.basic.priceMonthly,   period: 'mois', popular: false },
  { id: 'premium', tier: 'premium', label: 'Premium', price: TIERS.premium.priceMonthly, period: 'mois', popular: true,  badge: 'Populaire' },
  { id: 'gold',    tier: 'gold',    label: 'Gold',    price: TIERS.gold.priceMonthly,    period: 'mois', popular: false, badge: 'Sans limite' },
];

export const PREMIUM_PLANS_YEARLY = [
  { id: 'basic_yearly',   tier: 'basic',   label: 'Basic',   price: TIERS.basic.priceYearlyMonth,   period: 'mois', yearly: TIERS.basic.priceYearly,   popular: false },
  { id: 'premium_yearly', tier: 'premium', label: 'Premium', price: TIERS.premium.priceYearlyMonth, period: 'mois', yearly: TIERS.premium.priceYearly, popular: true,  badge: 'Populaire' },
  { id: 'gold_yearly',    tier: 'gold',    label: 'Gold',    price: TIERS.gold.priceYearlyMonth,    period: 'mois', yearly: TIERS.gold.priceYearly,    popular: false, badge: 'Sans limite' },
];

// ═══ FIX 2026-04 (v1.7.86) : sous-titres alignés sur TIERS ═══
// Avant : "15 → ∞ pièces" et "5 à 50 analyses" → désynchros avec TIERS
//   réel (10 → ∞ pièces, 3 → ∞ analyses).
// Maintenant : valeurs basées directement sur TIERS pour rester cohérent.
export const PREMIUM_FEATURES = [
  // v1.12.594 — valeurs exactes (pas ∞), cashback en €/100€ (pas %)
  { icon: '🧥', label: 'Dressing étendu',       sub: `${TIERS.free.wardrobe_items} → ${TIERS.gold.wardrobe_items} pièces selon l'abonnement` },
  { icon: '🤖', label: 'Plus d\'analyses IA',    sub: `${TIERS.free.ai_requests} → ${TIERS.gold.ai_requests} analyses par jour` },
  { icon: '✨', label: 'Looks illimités',         sub: 'Dès l\'abonnement Premium' },
  { icon: '🪞', label: 'Essais virtuels sur mannequin', sub: `${TIERS.basic.tryons_per_month}/m (Basic) · ${TIERS.premium.tryons_per_month}/m (Premium) · ${TIERS.gold.tryons_per_month}/m (Gold)` },
  { icon: '✈️', label: 'Voyages',                 sub: 'Planifiez vos valises (dès Basic)' },
  { icon: '🛍️', label: 'Suggestions exclusives',  sub: 'Articles sélectionnés par notre IA' },
  { icon: '⭐', label: 'Cashback préservé',      sub: `Jusqu'à ${TIERS.gold.cashback?.displayMaxEurPer100?.toFixed(2).replace('.', ',') || '4,80'} € pour 100 € d'achat (Gold)` },
];

// ─── Clés AsyncStorage par utilisateur ────────────────────────
function keyPlan(uid)    { return `outfit_plan_${uid    || 'guest'}`; }
function keyTier(uid)    { return `outfit_tier_${uid    || 'guest'}`; }
function keyTierUpdated(uid) { return `outfit_tier_updated_${uid || 'guest'}`; }

// v1.13.21 — Purge TOTALE du cache tier local (tier + plan + timestamp).
// À appeler quand le backend dit que l'user est free / abonnement expiré, pour
// qu'aucun fallback de getTier() ne ressuscite un tier payant périmé.
export async function purgeTierCache(userId) {
  const uid = userId || 'guest';
  try {
    await AsyncStorage.multiRemove([keyTier(uid), keyPlan(uid), keyTierUpdated(uid)]);
  } catch {
    try { await AsyncStorage.removeItem(keyTier(uid)); } catch {}
    try { await AsyncStorage.removeItem(keyPlan(uid)); } catch {}
    try { await AsyncStorage.removeItem(keyTierUpdated(uid)); } catch {}
  }
}
function keyAiCount(uid) { return `outfit_ai_count_${uid || 'guest'}`; }
function keyAiDate(uid)  { return `outfit_ai_date_${uid  || 'guest'}`; }

// ─── Récupérer le tier effectif ──────────────────────────────
// ═══ FIX 2026-04 : AsyncStorage est LA source de vérité ═══
// Raison : après un upgrade, saveTierLocally() met à jour AsyncStorage
// immédiatement MAIS `user.tier` en React state ne se met à jour qu'après
// `updateUserProfile({ tier })` + re-render. Entre les deux, des appels
// à getTier utilisaient l'ancien `user.tier` → tier considéré free alors
// que l'user vient d'upgrade → popup "8/3 plan Gratuit" s'affichait à tort.
//
// Ordre de priorité maintenant :
//  1. AsyncStorage (source de vérité locale, modifiée par saveTierLocally)
//  2. user.tier (fallback si storage indisponible)
//  3. legacy plan
//  4. 'free' par défaut
export async function getTier(user) {
  const uid = user?.id || user?.sub;

  // ═══ FIX 2026-04 : check expiration AVANT de renvoyer un tier payant ═══
  // Si user.premiumExpiresAt est dans le passé, l'abonnement n'est plus actif.
  // MAIS si AsyncStorage a un tier mis à jour APRÈS la date d'expiration
  // affichée par le client, c'est qu'une réactivation a eu lieu et que le
  // user.premiumExpiresAt n'est pas encore à jour côté client (re-fetch DB
  // pas encore fait). Dans ce cas on fait confiance au tier local.
  const expiresAt = user?.premiumExpiresAt;
  let isExpired = false;
  if (expiresAt) {
    const t = new Date(expiresAt).getTime();
    if (!isNaN(t) && t < Date.now()) {
      isExpired = true;
    }
  }
  if (isExpired && uid) {
    try {
      const tierUpdatedRaw = await AsyncStorage.getItem(keyTierUpdated(uid));
      const tierUpdatedAt = tierUpdatedRaw ? parseInt(tierUpdatedRaw, 10) : 0;
      const expiresAtMs = expiresAt ? new Date(expiresAt).getTime() : 0;
      // ═══ FIX 2026-06 (bug tier Gold fantôme) ═══
      // AVANT : `tierUpdatedAt > expiresAtMs` suffisait à « ressusciter » le tier.
      // Or saveTierLocally() ET la réconciliation /profile réécrivaient ce timestamp
      // à CHAQUE lancement → un cache Gold périmé de plusieurs jours restait considéré
      // « actif » indéfiniment (user voyait 500 slots / 40 try-ons alors qu'il est free).
      // MAINTENANT : on n'honore le local QUE si la mise à jour est RÉCENTE (< 10 min) —
      // une vraie réactivation se reflète dans /profile en quelques minutes ; un cache
      // vieux de plusieurs heures/jours est forcément périmé.
      const RECENT_RENEWAL_MS = 10 * 60 * 1000;
      if (tierUpdatedAt > expiresAtMs && (Date.now() - tierUpdatedAt) < RECENT_RENEWAL_MS) {
        isExpired = false;
        console.log(`[getTier] réactivation récente (${new Date(tierUpdatedAt).toISOString()}) → tier local conservé`);
      }
    } catch {}
  }
  if (isExpired) {
    // Abonnement expiré, pas de réactivation récente → free. On PURGE le cache tier
    // périmé pour qu'aucun fallback (storedTier / plan) ne ré-injecte un tier payant
    // au prochain appel (auto-réparation).
    try { await purgeTierCache(uid); } catch {}
    return 'free';
  }

  // ═══ FIX v1.7.99 (2026-04-28) : priorité à user.tier en mémoire ═══
  // Avant : AsyncStorage était lu en premier. Si l'app avait un tier_${uid} = 'free'
  //   périmé en cache (ex: avant activation server-side), getTier renvoyait 'free'
  //   alors que user.tier === 'premium' (récemment chargé depuis l'API).
  //   → Bug visible dans LooksScreen : isUnlimited=false alors que Profil
  //     affiche correctement "PREMIUM ACTIF".
  // Maintenant : on regarde d'abord user.tier (memoire fraîche depuis l'API).
  //   AsyncStorage devient un fallback pour l'offline / 1er render.
  const memTier = user?.tier;
  if (memTier && TIERS[memTier]) return memTier;

  // 1. AsyncStorage en fallback
  if (uid) {
    try {
      const storedTier = await AsyncStorage.getItem(keyTier(uid));
      if (storedTier && TIERS[storedTier]) return storedTier;

      const storedPlan = await AsyncStorage.getItem(keyPlan(uid));
      if (storedPlan === 'monthly' || storedPlan === 'yearly' || storedPlan === 'premium') {
        return 'premium';
      }
    } catch {}
  }

  // 3. legacy plan
  const legacyPlan = user?.plan;
  if (legacyPlan === 'monthly' || legacyPlan === 'yearly' || legacyPlan === 'premium') {
    return 'premium';
  }

  return 'free';
}

// ─── Vérifier si l'abonnement est techniquement expiré ──────
// Différent de getTier() : retourne true si l'user était abonné mais que
// la subscription a expiré (date dépassée). Permet d'afficher un état
// "expiré" élégant dans l'UI plutôt que faire disparaître le plan.
export function isSubscriptionExpired(user) {
  const expiresAt = user?.premiumExpiresAt;
  if (!expiresAt) return false;
  const t = new Date(expiresAt).getTime();
  if (isNaN(t)) return false;
  return t < Date.now();
}

export async function getLimits(user) {
  const tier = await getTier(user);
  return TIERS[tier] || TIERS.free;
}

export async function saveTierLocally(userId, tier) {
  if (!userId || !TIERS[tier]) return;
  // v1.12.369 — capture l'ancien tier pour différencier upgrade/downgrade/cancel
  let previousTier = null;
  try { previousTier = await AsyncStorage.getItem(keyTier(userId)); } catch {}
  try {
    await AsyncStorage.setItem(keyTier(userId), tier);
    await AsyncStorage.setItem(keyPlan(userId), tier === 'free' ? 'free' : 'monthly');
    // Timestamp de mise à jour : permet à getTier() de savoir si le tier
    // local a été modifié plus récemment que user.premiumExpiresAt
    // (qui peut être stale entre 2 fetch DB).
    await AsyncStorage.setItem(keyTierUpdated(userId), String(Date.now()));
    // v1.13.93 — CES DEUX EFFETS NE DOIVENT SE PRODUIRE QUE SI LE TIER A
    //   RÉELLEMENT CHANGÉ. Avant, ils s'exécutaient à CHAQUE saveTierLocally
    //   (donc à chaque rechargement de profil, focus d'écran, etc.). Conséquence :
    //   `tier_changed` était ré-émis en boucle → les écrans (Looks/Wardrobe/Profil)
    //   faisaient closePremium()/setShowPremium(false) → le paywall "abonnement/
    //   add-ons" qui venait de s'ouvrir (arrivée sur Dressing/Looks, clic +/Ajouter)
    //   se FERMAIT tout seul aussitôt ("s'affiche et disparaît"). On garde derrière
    //   un vrai changement de tier.
    const tierReallyChanged = String(previousTier || '') !== String(tier || '');
    if (tierReallyChanged) {
      // ═══ FIX 2026-04 : reset le flag quota IA après upgrade ═══
      // Free (3/3) → Basic (10/jour) : libère _aiQuotaExhausted immédiatement.
      try {
        const { resetAIQuotaFlag } = require('./AIStylistService');
        resetAIQuotaFlag();
        console.log(`[PremiumService] 🔓 tier changed ${previousTier}→${tier} → AI quota flag reset`);
      } catch {}
      // Event global pour que les écrans ferment le popup s'il était ouvert au
      // moment de l'upgrade (mais PAS sur un simple reload au même tier).
      try {
        const { DeviceEventEmitter } = require('react-native');
        DeviceEventEmitter.emit('tier_changed', { tier });
      } catch {}
    }
    // v1.12.369 — Google Analytics : event "subscription_changed"
    //   tier ∈ {free, basic, premium, gold} — couvre activate, upgrade, downgrade, cancel
    try {
      if (previousTier !== tier) {
        const { trackEvent } = require('./AnalyticsService');
        const PAID = ['basic', 'premium', 'gold'];
        const wasPaid = PAID.includes(previousTier);
        const isPaid = PAID.includes(tier);
        let kind = 'change';
        if (!wasPaid && isPaid) kind = 'activate';
        else if (wasPaid && !isPaid) kind = 'cancel';
        else if (wasPaid && isPaid) {
          const rank = { basic: 1, premium: 2, gold: 3 };
          kind = (rank[tier] || 0) > (rank[previousTier] || 0) ? 'upgrade' : 'downgrade';
        }
        trackEvent('subscription_changed', {
          tier,
          previous_tier: previousTier || 'unknown',
          kind, // activate | cancel | upgrade | downgrade | change
        });
      }
    } catch {}
  } catch {}
}

// Alias legacy
export async function savePlanLocally(userId, plan) {
  return saveTierLocally(userId, plan === 'monthly' || plan === 'yearly' ? 'premium' : plan);
}

export async function isPremium(user) {
  const tier = await getTier(user);
  return PAID_TIERS.includes(tier);
}

export async function hasFeatureAccess(user, feature) {
  const tier = await getTier(user);
  const t = TIERS[tier];
  switch (feature) {
    case 'travel':          return t.trips_access === true;
    case 'looks_unlimited': return t.looks_per_day === Infinity;
    default: return true;
  }
}

export async function getAIUsageToday(userId, user = null) {
  // ═══ Source de vérité : backend /ai-usage/status ═══
  // On lit depuis la DB. Si backend down, fallback local.
  if (user?.accessToken) {
    try {
      const { AWS_CONFIG } = require('../config/aws_config');
      const res = await fetch(`${AWS_CONFIG.apiUrl}/ai-usage/status`, {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${user.accessToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        // Sync le local pour que les autres composants soient cohérents
        try {
          const today = new Date().toDateString();
          await AsyncStorage.setItem(keyAiDate(userId), today);
          await AsyncStorage.setItem(keyAiCount(userId), String(data.current || 0));
        } catch {}
        return data.current || 0;
      }
    } catch {}
  }

  // ═══ Fallback local ═══
  try {
    const today     = new Date().toDateString();
    const savedDate = await AsyncStorage.getItem(keyAiDate(userId));
    const savedCount= await AsyncStorage.getItem(keyAiCount(userId));
    if (savedDate !== today) {
      // Nouveau jour → reset automatique à 0
      await AsyncStorage.setItem(keyAiDate(userId),  today);
      await AsyncStorage.setItem(keyAiCount(userId), '0');
      console.log(`[AI] Reset compteur (nouveau jour) user=${userId}`);
      return 0;
    }
    return parseInt(savedCount || '0', 10);
  } catch { return 0; }
}

/**
 * Reset manuel du compteur AI si la date a changé (appelé au démarrage de l'app).
 * Permet de garantir que le compteur affiché est toujours à jour,
 * même si l'utilisateur ouvre l'app après minuit sans avoir consommé d'IA.
 * @param {string} userId
 * @returns {Promise<boolean>} true si reset effectué
 */
export async function resetAIUsageIfNewDay(userId) {
  if (!userId) return false;
  try {
    const today     = new Date().toDateString();
    const savedDate = await AsyncStorage.getItem(keyAiDate(userId));
    if (savedDate !== today) {
      await AsyncStorage.setItem(keyAiDate(userId),  today);
      await AsyncStorage.setItem(keyAiCount(userId), '0');
      console.log(`[AI] ✓ Compteur AI reset à 0 (nouveau jour: ${today}) pour user=${userId}`);
      return true;
    }
    return false;
  } catch { return false; }
}

export async function incrementAIUsage(userId) {
  try {
    const today     = new Date().toDateString();
    const savedDate = await AsyncStorage.getItem(keyAiDate(userId));
    const savedCount= await AsyncStorage.getItem(keyAiCount(userId));
    const current   = (savedDate === today) ? parseInt(savedCount || '0', 10) : 0;
    const next      = current + 1;
    await AsyncStorage.setItem(keyAiDate(userId),  today);
    await AsyncStorage.setItem(keyAiCount(userId), String(next));
    console.log('[AI] user:', userId, '| count:', next);
    return next;
  } catch { return 0; }
}

// ─── Vérifier une limite ─────────────────────────────────────
export async function checkLimit(type, currentCount, user) {
  const userId = user?.id || user?.sub;
  const tier   = await getTier(user);
  const t      = TIERS[tier];

  switch (type) {
    case 'wardrobe': {
      if (t.wardrobe_items === Infinity) return { allowed: true, tier, premium: tier !== 'free' };
      // v1.12.957 — limite = quota TOTAL backend (tier + boost +50 + abos wardrobe),
      // pas juste t.wardrobe_items. Sans ça, après un boost +50 le bouton "+"
      // proposait quand même d'acheter alors que l'user avait de la place.
      let totalQuota = t.wardrobe_items;
      try {
        if (user?.accessToken) {
          const { AWS_CONFIG } = require('../config/aws_config');
          const b = await fetch(`${AWS_CONFIG.apiUrl}/addons/balance`, {
            headers: { Authorization: `Bearer ${user.accessToken}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null);
          const ta = b?.wardrobeQuota?.totalAvailable;
          if (typeof ta === 'number' && ta > 0) totalQuota = ta;
        }
      } catch {}
      return {
        allowed: currentCount < totalQuota,
        premium: tier !== 'free',
        tier,
        limit:   totalQuota,
        current: currentCount,
        reason:  `Dressing limité à ${totalQuota} pièces`,
        trigger: 'wardrobe',
      };
    }

    case 'ai': {
      // v1.13.57 — SOURCE DE VÉRITÉ = /ai-usage/status (serveur). Son `limit`
      // inclut DÉJÀ les scans achetés (packs addon_scans) et il renvoie `allowed`.
      // On l'utilise DIRECTEMENT → le gate client ne peut plus diverger du serveur.
      // Token ROBUSTE : user.accessToken OU SecureStore (authHelpers). Le fix
      // précédent (v1.13.55) ne comptait les scans que si user.accessToken était
      // présent — or il manquait souvent dans ce contexte → "quota atteint" à tort
      // malgré des scans achetés (la cliente restait bloquée pour ajouter au dressing).
      const _getTok = async () => {
        let tok = user?.accessToken;
        if (!tok) { try { tok = await require('./authHelpers').getAuthToken(); } catch {} }
        return tok;
      };
      try {
        const { AWS_CONFIG } = require('../config/aws_config');
        const token = await _getTok();
        if (token) {
          const st = await fetch(`${AWS_CONFIG.apiUrl}/ai-usage/status`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null);
          if (st && typeof st.limit === 'number' && typeof st.current === 'number') {
            const allowed = st.limit === -1 || st.current < st.limit;
            console.log(`[PremiumService.checkLimit ai/server] tier=${st.tier} used=${st.current} limit=${st.limit} allowed=${allowed}`);
            return {
              allowed,
              premium: (st.tier || tier) !== 'free',
              tier:    st.tier || tier,
              limit:   st.limit,
              current: st.current,
              reason:  `${t.ai_requests} analyses IA par jour en ${t.label}`,
              trigger: 'ai',
            };
          }
        }
      } catch {}
      // ─── Fallback (serveur /ai-usage/status injoignable) ───
      // v1.13.66 — FAIL-OPEN si on ne peut pas CONFIRMER le solde de scans achetés.
      // Cas Val (v1.13.63) : le fetch /ai-usage/status échouait (token/réseau) →
      // on retombait sur la limite gratuite (5) SANS compter ses 25 scans → "quota
      // exceeded" à tort malgré un achat. Désormais : on tente /addons/balance ;
      // si le solde est INCONNU (fetch KO), on N'ASSUME PAS 0 scan → on AUTORISE.
      // Le serveur reste autoritaire (analyze-item / check-and-increment comptent
      // les scans et bloquent réellement si dépassement). Ne JAMAIS bloquer à tort
      // un user payant vaut mieux qu'un fail-open (le serveur rattrape).
      const used = await getAIUsageToday(userId, user);
      let limit = t.ai_requests;
      let balanceKnown = false;
      try {
        const token = await _getTok();
        if (Number.isFinite(limit) && token) {
          const { AWS_CONFIG } = require('../config/aws_config');
          const b = await fetch(`${AWS_CONFIG.apiUrl}/addons/balance`, {
            headers: { Authorization: `Bearer ${token}` },
          }).then(r => r.ok ? r.json() : null).catch(() => null);
          if (b && typeof b.scans === 'number') {
            balanceKnown = true;
            if (b.scans > 0) limit += b.scans;
          }
        }
      } catch {}
      if (!balanceKnown) {
        console.warn('[PremiumService.checkLimit ai/local] solde scans INCONNU → fail-open (serveur autoritaire)');
        return {
          allowed: true, premium: tier !== 'free', tier, limit, current: used,
          reason: `${t.ai_requests} analyses IA par jour en ${t.label}`, trigger: 'ai',
        };
      }
      const allowed = used < limit;
      console.log(`[PremiumService.checkLimit ai/local] tier=${tier} used=${used} limit=${limit} (tierBase=${t.ai_requests}) allowed=${allowed}`);
      return {
        allowed,
        premium: tier !== 'free',
        tier,
        limit,
        current: used,
        reason:  `${t.ai_requests} analyses IA par jour en ${t.label}`,
        trigger: 'ai',
      };
    }

    case 'looks':
      if (t.looks_per_day === Infinity) return { allowed: true, tier, premium: tier !== 'free' };
      return {
        allowed: currentCount < t.looks_per_day,
        premium: tier !== 'free',
        tier,
        limit:   t.looks_per_day,
        current: currentCount,
        reason:  `Limité à ${t.looks_per_day} tenues par jour en ${t.label}`,
        trigger: 'looks',
      };

    case 'trips':
      // Seul Free et Basic n'ont PAS accès au voyage
      if (!t.trips_access) {
        return {
          allowed: false,
          premium: tier !== 'free',
          tier,
          limit:   0,
          current: currentCount,
          reason:  `Passez en Premium ou Gold pour accéder aux voyages`,
          trigger: 'trips',
        };
      }
      return {
        allowed: currentCount < t.trips_count,
        premium: true,
        tier,
        limit:   t.trips_count,
        current: currentCount,
        reason:  `${t.trips_count} voyages maximum en ${t.label}`,
        trigger: 'trips',
      };

    default:
      return { allowed: true, premium: tier !== 'free', tier };
  }
}

// ─── Helpers UI ──────────────────────────────────────────────
export function getTierByID(tierId) { return TIERS[tierId] || TIERS.free; }

/**
 * Vérifiez si l'utilisateur peut encore utiliser l'IA aujourd'hui.
 * - Si quota disponible → incrémente et retourne { allowed: true }
 * - Si quota dépassé → NE PAS incrémenter, retourne { allowed: false, limit, current, tier }
 *
 * À utiliser AVANT tout appel IA dans les écrans.
 *
 * Usage :
 *   const res = await canUseAIOrIncrement(user);
 *   if (!res.allowed) {
 *     // Déclencher upgrade popup
 *     triggerUpgrade('ai', res.current);
 *     return;
 *   }
 *   // Appel IA autorisé
 */
export async function canUseAIOrIncrement(user) {
  if (!user) return { allowed: false, current: 0, limit: 0, tier: 'free' };
  const userId = user?.id || user?.sub;

  // ═══ Source de vérité : backend Lambda ═══
  // On appelle /ai-usage/check-and-increment qui fait tout atomiquement :
  // - lit le tier depuis la DB subscriptions
  // - vérifiez le count du jour
  // - incrémente si allowed
  // - retourne allowed/current/limit/tier
  // Si le backend est down, on fallback sur le count local (fail-open).
  try {
    const { AWS_CONFIG } = require('../config/aws_config');
    const token = user?.accessToken;
    if (!token) throw new Error('no token');

    const res = await fetch(`${AWS_CONFIG.apiUrl}/ai-usage/check-and-increment`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();

    // Sync le compteur local pour l'affichage (sera écrasé par getAIUsageToday)
    if (data.current != null) {
      try {
        const today = new Date().toDateString();
        await AsyncStorage.setItem(keyAiDate(userId), today);
        await AsyncStorage.setItem(keyAiCount(userId), String(data.current));
      } catch {}
      // ═══ Émettre un event pour rafraîchir les compteurs dans l'UI ═══
      // (ProfileScreen, et tout autre écran qui affiche le compteur AI)
      try {
        const { DeviceEventEmitter } = require('react-native');
        DeviceEventEmitter.emit('ai_usage_updated', {
          current: data.current,
          limit: data.limit,
          tier: data.tier,
        });
      } catch {}
    }

    if (!data.allowed) {
      console.log(`[AI Limit] BLOCKED (backend) tier=${data.tier} ${data.current}/${data.limit}`);
      // v1.12.370 — GA4 event: ai_analysis_blocked (limite atteinte)
      try {
        const { trackEvent } = require('./AnalyticsService');
        trackEvent('ai_analysis_blocked', {
          tier: data.tier || 'free',
          current: data.current || 0,
          limit: data.limit || 0,
          source: 'backend',
        });
      } catch {}
      return {
        allowed: false,
        tier: data.tier || 'free',
        current: data.current || 0,
        limit: data.limit || 0,
        reason: ((() => { let fr = true; try { fr = require('../i18n').getCurrentLang().startsWith('fr'); } catch {} return fr; })())
          ? `Limite IA atteinte (${data.current}/${data.limit} aujourd'hui). Reprise demain à 00:00 ou passez à un plan supérieur.`
          : `AI limit reached (${data.current}/${data.limit} today). Resets tomorrow at midnight, or upgrade for more scans.`,
      };
    }

    // v1.12.370 — GA4 event: ai_analysis_used (analyse autorisée, quota incrémenté)
    try {
      const { trackEvent } = require('./AnalyticsService');
      trackEvent('ai_analysis_used', {
        tier: data.tier || 'free',
        current: data.current || 0,
        limit: data.limit || 0,
        source: 'backend',
      });
    } catch {}

    return {
      allowed: true,
      tier: data.tier || 'free',
      current: data.current,
      limit: data.limit,
    };
  } catch (e) {
    console.warn('[AI Limit] backend unavailable, fallback local:', e.message);
    // ═══ Fallback local (offline ou backend down) ═══
    const tier = await getTier(user);
    const t = TIERS[tier] || TIERS.free;
    const used = await getAIUsageToday(userId);

    if (used >= t.ai_requests) {
      // v1.12.370 — GA4 event: ai_analysis_blocked (fallback local)
      try {
        const { trackEvent } = require('./AnalyticsService');
        trackEvent('ai_analysis_blocked', {
          tier,
          current: used,
          limit: t.ai_requests,
          source: 'local',
        });
      } catch {}
      return {
        allowed: false,
        tier,
        current: used,
        limit: t.ai_requests,
        reason: ((() => { let fr = true; try { fr = require('../i18n').getCurrentLang().startsWith('fr'); } catch {} return fr; })())
          ? `Limite IA atteinte (${used}/${t.ai_requests} analyses aujourd'hui).`
          : `AI limit reached (${used}/${t.ai_requests} analyses today).`,
      };
    }
    const newCount = await incrementAIUsage(userId);
    // Émettre event même en fallback local
    try {
      const { DeviceEventEmitter } = require('react-native');
      DeviceEventEmitter.emit('ai_usage_updated', {
        current: newCount,
        limit: t.ai_requests,
        tier,
      });
    } catch {}
    // v1.12.370 — GA4 event: ai_analysis_used (fallback local)
    try {
      const { trackEvent } = require('./AnalyticsService');
      trackEvent('ai_analysis_used', {
        tier,
        current: newCount,
        limit: t.ai_requests,
        source: 'local',
      });
    } catch {}
    return { allowed: true, tier, current: newCount, limit: t.ai_requests };
  }
}

export function getTierBadge(tierId) {
  const t = TIERS[tierId] || TIERS.free;
  const labels = {
    free:    { emoji: '',   text: 'FREE' },
    basic:   { emoji: '⭐', text: 'BASIC' },
    premium: { emoji: '✨', text: 'PREMIUM' },
    gold:    { emoji: '👑', text: 'GOLD' },
  };
  return { ...(labels[tierId] || labels.free), color: t.color };
}
